/**
 * Telegram service with anti-spam: cooldowns, rate limits, deduplication
 */

const STORAGE_KEY = "hodl_telegram_last_sent";

interface SentRecord {
  [key: string]: number; // signalKey -> timestamp
}

const COOLDOWN_MS = 60 * 60 * 1000; // 1 hour per (signalType, symbol)
const MAX_PER_HOUR = 5;
const HOURLY_WINDOW_MS = 60 * 60 * 1000;

function getRecords(): SentRecord {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveRecords(records: SentRecord) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    // ignore
  }
}

function pruneOld(records: SentRecord) {
  const now = Date.now();
  const pruned: SentRecord = {};
  for (const [k, v] of Object.entries(records)) {
    if (now - v < COOLDOWN_MS * 2) pruned[k] = v; // keep for 2x cooldown
  }
  return pruned;
}

function canSend(signalKey: string): boolean {
  const records = pruneOld(getRecords());
  const last = records[signalKey];
  if (last && Date.now() - last < COOLDOWN_MS) return false;

  const recent = Object.values(records).filter((t) => Date.now() - t < HOURLY_WINDOW_MS);
  if (recent.length >= MAX_PER_HOUR) return false;

  return true;
}

function markSent(signalKey: string) {
  const records = pruneOld(getRecords());
  records[signalKey] = Date.now();
  saveRecords(records);
}

export async function sendTelegramSignal(message: string, signalKey: string): Promise<boolean> {
  const token = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
  const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;

  if (!token || !chatId || token === "YOUR_BOT_TOKEN") return false;
  if (!canSend(signalKey)) return false;

  try {
    const res = await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        chat_id: chatId,
        text: message,
        disable_web_page_preview: true,
      }),
    });
    if (res.ok) markSent(signalKey);
    return res.ok;
  } catch (err) {
    console.error("Telegram send failed:", err);
    return false;
  }
}

export function isTelegramConfigured(): boolean {
  const token = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
  const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;
  return !!(token && chatId && token !== "YOUR_BOT_TOKEN");
}
